import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { startOfMonth } from 'date-fns';

import { DragAssignmentItem } from 'components/CalendarDragContext';
import { DropdownOption } from 'components/Dropdown';

import CalculatedStudentAssignmentStatus from 'enums/CalculatedStudentAssignmentStatus';
import CalendarView from 'enums/CalendarView';

import {
  CourseOptionFragment,
  StudentOptionFragment,
} from 'graphql/types/generated';
import { Student_Assignment_Status_Enum } from 'graphql/types/schema';

import { getDaysInWeek } from 'utils/getDays';

import { CalendarFiltersState, CalendarState, RootState } from './types';

export function getSelectionKey(
  studentAssignmentId: string,
  sourceDate: string
): string {
  return `${studentAssignmentId}::${sourceDate}`;
}

export const CALENDAR_HIDE_COMPLETED_KEY =
  'syllabird.calendar.filters.hideCompleted';

export const CALENDAR_VIEW_KEY = 'syllabird.calendar.view';
export const CALENDAR_VIEW_PARAM = 'view';

const isCalendarView = (value: string | null): value is CalendarView =>
  value !== null && Object.values(CalendarView).includes(value as CalendarView);

const loadInitialCalendarView = (): CalendarView => {
  try {
    const paramView = new URLSearchParams(window.location.search).get(
      CALENDAR_VIEW_PARAM
    );
    if (isCalendarView(paramView)) {
      return paramView;
    }

    const savedView = localStorage?.getItem(CALENDAR_VIEW_KEY);
    if (isCalendarView(savedView)) {
      return savedView;
    }
  } catch (error) {
    console.warn('Failed to fetch calendar view state', error);
  }

  return CalendarView.Week;
};

/**
 * Reducers
 */
const loadSavedHideCompleted = (): boolean => {
  try {
    return (
      JSON.parse(
        localStorage?.getItem(CALENDAR_HIDE_COMPLETED_KEY) ?? 'false'
      ) ?? false
    );
  } catch (error) {
    console.warn('Failed to fetch calendar filter state', error);
  }

  return false;
};

const loadInitialState = (): CalendarState => {
  const savedHideCompleted = loadSavedHideCompleted();

  return {
    view: loadInitialCalendarView(),
    currentWeek: getDaysInWeek(new Date()).map((d) => d.toISOString()),
    currentMonthAnchor: startOfMonth(new Date()).toISOString(),
    filters: {
      students: [],
      courses: [],
      subjects: [],
      assignmentTypes: [],
      assignmentStatuses: [],
      hideCompleted: savedHideCompleted,
    },
    selection: {
      selectionMap: {},
      selectModeActive: false,
    },
  };
};

export const calendarSlice = createSlice({
  name: 'calendar',
  initialState: loadInitialState(),
  reducers: {
    setCurrentWeek: (state: CalendarState, action: PayloadAction<string[]>) => {
      state.currentWeek = action.payload;
    },
    setCalendarView: (
      state: CalendarState,
      action: PayloadAction<CalendarView>
    ) => {
      state.view = action.payload;
      if (action.payload === CalendarView.Month) {
        state.selection.selectionMap = {};
        state.selection.selectModeActive = false;
      }
    },
    setCurrentMonthAnchor: (
      state: CalendarState,
      action: PayloadAction<string>
    ) => {
      state.currentMonthAnchor = action.payload;
    },
    setCalendarStudentsFilter: (
      state: CalendarState,
      action: PayloadAction<DropdownOption<StudentOptionFragment>[]>
    ) => {
      state.filters.students = action.payload;
    },
    setCalendarCoursesFilter: (
      state: CalendarState,
      action: PayloadAction<DropdownOption<CourseOptionFragment>[]>
    ) => {
      state.filters.courses = action.payload;
    },
    setCalendarSubjectsFilter: (
      state: CalendarState,
      action: PayloadAction<DropdownOption<string>[]>
    ) => {
      state.filters.subjects = action.payload;
    },
    setCalendarAssignmentTypesFilter: (
      state: CalendarState,
      action: PayloadAction<DropdownOption<string>[]>
    ) => {
      state.filters.assignmentTypes = action.payload;
    },
    setCalendarAssignmentStatusesFilter: (
      state: CalendarState,
      action: PayloadAction<
        DropdownOption<
          Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
        >[]
      >
    ) => {
      state.filters.assignmentStatuses = action.payload;
    },
    toggleCalendarHideCompleted: (state: CalendarState) => {
      const hideCompletedNext = !state.filters.hideCompleted;
      state.filters.hideCompleted = hideCompletedNext;

      if (hideCompletedNext) {
        state.filters.assignmentStatuses =
          state.filters.assignmentStatuses.filter(
            (status) => status.value !== Student_Assignment_Status_Enum.Complete
          );
      }
    },
    clearCalendarFilters: (state: CalendarState) => {
      state.filters = loadInitialState().filters;
    },
    toggleCalendarSelection: (
      state: CalendarState,
      action: PayloadAction<DragAssignmentItem>
    ) => {
      const data = action.payload;
      const key = getSelectionKey(data.studentAssignmentId, data.sourceDate);
      if (key in state.selection.selectionMap) {
        delete state.selection.selectionMap[key];
      } else {
        state.selection.selectionMap[key] = data;
      }
    },
    selectCalendarAssignments: (
      state: CalendarState,
      action: PayloadAction<DragAssignmentItem[]>
    ) => {
      const items = action.payload;
      const keys = items.map((item) =>
        getSelectionKey(item.studentAssignmentId, item.sourceDate)
      );
      const allAlreadySelected = keys.every(
        (key) => key in state.selection.selectionMap
      );

      if (allAlreadySelected) {
        keys.forEach((key) => {
          delete state.selection.selectionMap[key];
        });
      } else {
        items.forEach((item) => {
          const key = getSelectionKey(
            item.studentAssignmentId,
            item.sourceDate
          );
          state.selection.selectionMap[key] = item;
        });
      }
    },
    enterCalendarSelectMode: (state: CalendarState) => {
      state.selection.selectModeActive = true;
    },
    clearCalendarSelection: (state: CalendarState) => {
      state.selection.selectionMap = {};
      state.selection.selectModeActive = false;
    },
  },
});

/**
 * Actions
 */
export const {
  setCurrentWeek,
  setCalendarView,
  setCurrentMonthAnchor,
  setCalendarStudentsFilter,
  setCalendarCoursesFilter,
  setCalendarSubjectsFilter,
  setCalendarAssignmentTypesFilter,
  setCalendarAssignmentStatusesFilter,
  toggleCalendarHideCompleted,
  clearCalendarFilters,
  toggleCalendarSelection,
  selectCalendarAssignments,
  enterCalendarSelectMode,
  clearCalendarSelection,
} = calendarSlice.actions;

/**
 * Selectors
 */
const getCalendar = (state: RootState): CalendarState => state.calendar;

const getCurrentWeekStrings = createSelector(
  getCalendar,
  (calendar: CalendarState) => calendar.currentWeek
);

export const getCurrentWeek = createSelector(
  getCurrentWeekStrings,
  (weekStrings) => weekStrings.map((s) => new Date(s))
);

export const getCalendarView = createSelector(
  getCalendar,
  (calendar: CalendarState) => calendar.view
);

export const getCurrentMonthAnchor = createSelector(
  getCalendar,
  (calendar: CalendarState) => new Date(calendar.currentMonthAnchor)
);

// Filter Selectors
export const getCalendarFilters = createSelector(
  getCalendar,
  (calendar: CalendarState) => calendar.filters
);

export const getCalendarStudentsFilter = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.students
);

export const getCalendarCoursesFilter = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.courses
);

export const getCalendarSubjectsFilter = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.subjects
);

export const getCalendarAssignmentTypesFilter = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.assignmentTypes
);

export const getCalendarAssignmentStatusesFilter = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.assignmentStatuses
);

export const getCalendarHideCompleted = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => filters.hideCompleted
);

export const getCalendarNumFiltersApplied = createSelector(
  getCalendarFilters,
  (filters: CalendarFiltersState) => {
    const {
      students,
      courses,
      subjects,
      assignmentTypes,
      assignmentStatuses,
      hideCompleted,
    } = filters;

    return (
      (students?.length ?? 0) +
      (courses?.length ?? 0) +
      (subjects?.length ?? 0) +
      (assignmentTypes?.length ?? 0) +
      (assignmentStatuses?.length ?? 0) +
      (hideCompleted ? 1 : 0)
    );
  }
);

export const getCalendarFilteredStudentIds = createSelector(
  getCalendarStudentsFilter,
  (studentsFilter: DropdownOption<StudentOptionFragment>[]) =>
    studentsFilter.map((option) => option.value.id)
);

export const getCalendarFilteredCourseIds = createSelector(
  getCalendarCoursesFilter,
  (courses: DropdownOption<CourseOptionFragment>[]) =>
    courses.map((option) => option.value.id)
);

export const getCalendarFilteredSubjects = createSelector(
  getCalendarSubjectsFilter,
  (subjects: DropdownOption<string>[]) => subjects.map((option) => option.value)
);

export const getCalendarFilteredAssignmentTypeIds = createSelector(
  getCalendarAssignmentTypesFilter,
  (assignmentTypes: DropdownOption<string>[]) =>
    assignmentTypes.map((option) => option.value)
);

export const getCalendarFilteredAssignmentStatuses = createSelector(
  getCalendarAssignmentStatusesFilter,
  (
    assignmentStatuses: DropdownOption<
      Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
    >[]
  ) => assignmentStatuses.map((option) => option.value)
);

// Selection Selectors
const getCalendarSelection = createSelector(
  getCalendar,
  (calendar: CalendarState) => calendar.selection
);

export const getCalendarSelectionMap = createSelector(
  getCalendarSelection,
  (selection) => selection.selectionMap
);

export const getIsCalendarSelectMode = createSelector(
  getCalendarSelection,
  (selection) =>
    selection.selectModeActive || Object.keys(selection.selectionMap).length > 0
);

export const getSelectedCalendarAssignments = createSelector(
  getCalendarSelectionMap,
  (selectionMap) => {
    const seen = new Set<string>();
    return Object.values(selectionMap).flatMap((assignment) =>
      seen.has(assignment.studentAssignmentId)
        ? []
        : (seen.add(assignment.studentAssignmentId), [assignment])
    );
  }
);

export const getSelectedCalendarAssignmentCount = createSelector(
  getCalendarSelectionMap,
  (selectionMap) =>
    new Set(Object.values(selectionMap).map((a) => a.studentAssignmentId)).size
);
